import React from 'react';
import { string, bool, func, number } from 'prop-types';
import Arrow from '../svg/Arrow';
import {
  ButtonBase,
  Grid,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';

const useStyles = makeStyles(
  ({
    palette: { primary, text },
    fonts,
    spacing,
    breakpoints: { down },
  }) => ({
    formButton: {
      display: 'flex',
      color: text.primary,
      zIndex: 1,
      position: 'relative',
      margin: spacing(12, 0),
      cursor: ({ isDisabled }) =>
        isDisabled ? 'default' : 'pointer',
      '& .MuiTouchRipple-root': {
        color: 'transparent',
        zIndex: -1,
      },
    },
    formButtonAnimation: {
      '&:hover': {
        opacity: 0.8,
        '& svg': {
          animation: '$hoverEffect 1.5s infinite',
        },
      },
    },
    formButtonReverseAnimation: {
      '&:hover': {
        opacity: 0.8,
        '& svg': {
          animation: '$hoverEffectReverse 1.5s infinite',
        },
      },
    },
    formButtonText: {
      color: text.primary,
      fontSize: fonts.size.xm,
      fontWeight: '700',
      paddingRight: ({ inverted }) =>
        !inverted ? spacing(2) : spacing(0),
      paddingLeft: ({ inverted }) =>
        inverted ? spacing(2) : spacing(0),
      position: 'relative',
      zIndex: 1,
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: 3,
        left: ({ inverted }) => (inverted ? spacing(2) : 0),
        height: 7,
        width: `calc(100% - ${spacing(2)}px)`,
        background: primary.dark,
        opacity: 0.1,
        zIndex: -1,
      },
      [down(325)]: {
        fontSize: fonts.size.m,
      },
    },
    pageNumber: {
      fontSize: fonts.size.xm,
      padding: spacing(0, 2),
      [down(325)]: {
        fontSize: fonts.size.m,
      },
    },
    disabledContainer: {
      transition: 'opacity .3s',
      opacity: ({ isDisabled }) => (isDisabled ? 0.2 : 1),
    },
    '@keyframes hoverEffectReverse': {
      '0%': {
        transform: 'translateX(0) rotate(-180deg)',
      },
      '50%': {
        transform: 'translateX(-10px) rotate(-180deg)',
      },
      '100%': {
        transform: 'translateX(0) rotate(-180deg)',
      },
    },
    '@keyframes hoverEffect': {
      '0%': {
        transform: 'translateX(0)',
      },
      '50%': {
        transform: 'translateX(10px)',
      },
      '100%': {
        transform: 'translateX(0)',
      },
    },
  })
);

const FormButton = ({
  title,
  tailed,
  isDisabled,
  inverted,
  buttonType,
  onClick,
  currentPageNumber,
  totalPageNumber,
  ...props
}) => {
  const classes = useStyles({ isDisabled, inverted });
  return (
    <ButtonBase
      className={`${classes.formButton} ${
        inverted
          ? classes.formButtonReverseAnimation
          : classes.formButtonAnimation
      }`}
      type={buttonType}
      disabled={isDisabled}
      onClick={() => onClick && !isDisabled && onClick()}
      {...props}
    >
      {currentPageNumber && totalPageNumber && (
        <Typography className={classes.pageNumber}>
          {currentPageNumber}/{totalPageNumber}
        </Typography>
      )}
      <Grid
        container
        direction={inverted ? 'row-reverse' : 'row'}
        alignItems="center"
        className={classes.disabledContainer}
      >
        <Typography className={classes.formButtonText}>
          {title}
        </Typography>
        <Arrow tailed={tailed} right={inverted} />
      </Grid>
    </ButtonBase>
  );
};

FormButton.propTypes = {
  title: string,
  tailed: bool,
  isDisabled: bool,
  inverted: bool,
  buttonType: string,
  onClick: func,
  currentPageNumber: number,
  totalPageNumber: number,
};

FormButton.defaultProps = {
  tailed: false,
  buttonType: 'submit',
};

export default FormButton;
